// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import type { WeightUnitPlusTrays } from '@seedplanner/shared-utils';
import { formatOrderInput } from '../../utils/formatOrderInput';

interface RecurringOrderInputInput {
  readonly sizeAmount?: string;
  readonly quantity: string;
  readonly unit: string;
  readonly cropOrBlendName: string;
}
interface RecurringOrderInput {
  readonly customer: string;
  readonly inputs: readonly RecurringOrderInputInput[];
}

export const makeRecurringOrdersPDF = (
  jsPDF: typeof import('jspdf').jsPDF,
  {
    orders,
    title,
    subtitle,
    downloadTitle,
  }: {
    readonly orders: readonly RecurringOrderInput[];
    readonly title: string;
    readonly subtitle?: string;
    readonly downloadTitle: string;
  },
) => {
  const doc = new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: 'a4',
  });

  // Page dimensions and settings
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const contentWidth = pageWidth - 2 * margin;
  const borderRadius = 2; // Border radius
  let startY = subtitle ? 54 : 44; // Initial Y position for the first todo item
  const lineSpacing = 10; // Spacing between lines
  let currentPage = 1; // Track current page number

  const drawBorderAndFooter = () => {
    // Draw a rounded rectangle border
    doc.roundedRect(margin, margin, contentWidth, pageHeight - 2 * margin, borderRadius, borderRadius);
    // Page number
    doc.setFontSize(10);
    doc.text(`Page ${currentPage}`, pageWidth - margin, pageHeight - margin / 2, { align: 'right' });
    // App name
    doc.text('Microgreen Manager', pageWidth / 2, pageHeight - margin / 2, { align: 'center' });
  };

  const addNewPage = () => {
    doc.addPage();
    currentPage += 1;
    startY = currentPage === 1 ? 50 : margin + 10; // Adjust startY for title and date exclusion on subsequent pages
    drawBorderAndFooter(); // Draw border and footer for the new page
  };

  // Draw initial border and footer
  drawBorderAndFooter();

  if (currentPage === 1) {
    // Title and subtitle
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(title, pageWidth / 2, 30, { align: 'center' });
    if (subtitle) {
      doc.setFontSize(12);
      doc.text(subtitle, pageWidth / 2, 40, { align: 'center' });
    }
  }

  // Set font for orders
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);

  for (const order of orders) {
    if (startY > pageHeight - margin - 10) {
      addNewPage();
    }

    // Print customer subheader
    doc.setFontSize(14);
    doc.text(order.customer, margin + 10, startY);
    startY += 6; // Space before listing inputs

    for (const input of order.inputs) {
      const formattedInput = formatOrderInput({
        quantity: input.quantity,
        sizeAmount: input.sizeAmount,
        sizeUnit: input.unit as WeightUnitPlusTrays,
      });
      const text = `${input.cropOrBlendName} - ${formattedInput}`;
      const splitText = doc.splitTextToSize(text, contentWidth - 15) as string[]; // Split text if it overflows

      splitText.forEach((line, lineIndex) => {
        if (startY > pageHeight - margin - 10) {
          addNewPage();
        }
        doc.setFontSize(12);

        // Checkbox for the first line of split text only
        if (lineIndex === 0) {
          doc.rect(20, startY, 5, 5);
        }

        // Adjust text start position based on whether it's the first line of split text
        doc.text(line, 30, startY + 4);

        startY += lineSpacing - 2; // Space between inputs
      });
    }

    startY += 8; // Space between orders/customers
  }

  // Save the PDF
  doc.save(`${downloadTitle}.pdf`);
};
